import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  BookOpen,
  FileEdit,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Trash2,
  X,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../utils/apiService';
import '../../styles/module-shared.css';
import './education.css';
import {
  COURSE_STATUSES,
  LESSON_TYPES,
  emptyLessonForm,
  formatCourseDate,
  normalizeLesson,
  normalizeModule,
  validateLessonForm,
} from './courseUtils';

const THEME = getThemeColor('primary');

const ModuleLessons = () => {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courseTitle, setCourseTitle] = useState('');
  const [moduleInfo, setModuleInfo] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyLessonForm(1));
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadLessons = useCallback(async () => {
    const res = await apiService.getEducationLessonsList(moduleId);
    if (!res.success) {
      setLessons([]);
      throw new Error(res.error || res.message || 'Failed to load lessons');
    }
    const payload = res.data ?? res.result ?? [];
    setLessons((Array.isArray(payload) ? payload : []).map(normalizeLesson).filter(Boolean));
  }, [moduleId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [courseRes, moduleRes] = await Promise.all([
        apiService.getEducationCourseById(courseId),
        apiService.getEducationModuleById(moduleId),
      ]);

      if (!courseRes.success) {
        setError(courseRes.error || courseRes.message || 'Failed to load course');
        return;
      }
      if (!moduleRes.success) {
        setError(moduleRes.error || moduleRes.message || 'Failed to load module');
        return;
      }

      const course = courseRes.data ?? courseRes.result ?? {};
      const mod = normalizeModule(moduleRes.data ?? moduleRes.result);
      if (!mod) {
        setError('Module not found');
        return;
      }

      // Ensure module belongs to this course route
      if (mod.courseId && Number(mod.courseId) !== Number(courseId)) {
        setError('Module not found for this course');
        return;
      }

      setCourseTitle(course.title || course.Title || mod.courseTitle || 'Course');
      setModuleInfo(mod);
      await loadLessons();
    } catch (err) {
      setError(err.message || 'Failed to load lessons');
    } finally {
      setLoading(false);
    }
  }, [courseId, moduleId, loadLessons]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    const next =
      (lessons.reduce((max, l) => Math.max(max, l.displayOrder || 0), 0) || 0) + 1;
    setEditing(null);
    setForm(emptyLessonForm(next));
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (lesson) => {
    setEditing(lesson);
    setForm({
      title: lesson.title || '',
      description: lesson.description || '',
      lessonType: LESSON_TYPES.includes(lesson.lessonType) ? lesson.lessonType : 'Article',
      displayOrder: String(lesson.displayOrder || 1),
      status: lesson.isActive ? 'Active' : 'Inactive',
    });
    setFormError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setFormError('');
  };

  const saveLesson = async (e) => {
    e.preventDefault();
    const validation = validateLessonForm(form);
    if (validation) {
      setFormError(validation);
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description?.trim() || null,
        lessonType: form.lessonType,
        displayOrder: Number(form.displayOrder),
        status: form.status === 'Inactive' ? 'Inactive' : 'Active',
      };
      const res = editing
        ? await apiService.updateEducationLesson({
            courseLessonId: editing.id,
            ...payload,
          })
        : await apiService.createEducationLesson({
            moduleId: Number(moduleId),
            ...payload,
          });

      if (!res.success) {
        setFormError(res.error || res.message || 'Save failed');
        return;
      }

      showSuccess(res.message || (editing ? 'Lesson updated' : 'Lesson created'));
      setModalOpen(false);
      setEditing(null);
      await loadLessons();
    } catch (err) {
      setFormError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runConfirm = async () => {
    if (!confirmAction?.lesson) return;
    const { type, lesson } = confirmAction;
    setActionBusy(true);
    try {
      let res;
      if (type === 'activate') res = await apiService.activateEducationLesson(lesson.id);
      else if (type === 'inactivate') res = await apiService.inactivateEducationLesson(lesson.id);
      else if (type === 'delete') res = await apiService.deleteEducationLesson(lesson.id);
      else return;

      if (!res.success) {
        showError(res.error || res.message || 'Action failed');
        return;
      }
      showSuccess(res.message || 'Updated successfully');
      setConfirmAction(null);
      await loadLessons();
    } catch (err) {
      showError(err.message || 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  const moveLesson = async (lesson, direction) => {
    setActionBusy(true);
    try {
      const res = await apiService.moveEducationLesson(lesson.id, direction);
      if (!res.success) {
        showError(res.error || res.message || 'Failed to move lesson');
        return;
      }
      const payload = res.data ?? res.result;
      if (Array.isArray(payload)) {
        setLessons(payload.map(normalizeLesson).filter(Boolean));
      } else {
        await loadLessons();
      }
      showSuccess(res.message || 'Lesson order updated');
    } catch (err) {
      showError(err.message || 'Failed to move lesson');
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="page edu-page" style={{ '--edu-primary': THEME }}>
        <div className="page-inner">
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        </div>
      </div>
    );
  }

  if (error && !moduleInfo) {
    return (
      <div className="page edu-page" style={{ '--edu-primary': THEME }}>
        <div className="page-inner">
          <button
            type="button"
            className="edu-back"
            onClick={() => navigate(`/education/courses/${courseId}`)}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to course
          </button>
          <ModuleAlert message={error} />
        </div>
      </div>
    );
  }

  const confirmCopy =
    confirmAction?.type === 'delete'
      ? 'Are you sure you want to delete this lesson?'
      : confirmAction?.type === 'inactivate'
        ? 'Are you sure you want to make this lesson inactive?'
        : confirmAction?.type === 'activate'
          ? 'Are you sure you want to activate this lesson?'
          : '';

  return (
    <div className="page edu-page" style={{ '--edu-primary': THEME }}>
      <div className="page-inner">
        <button
          type="button"
          className="edu-back"
          onClick={() => navigate(`/education/courses/${courseId}`)}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to modules
        </button>

        <nav className="edu-breadcrumb" aria-label="Breadcrumb">
          <Link to="/education/courses">Courses</Link>
          <span>/</span>
          <Link to={`/education/courses/${courseId}`}>{courseTitle || 'Course'}</Link>
          <span>/</span>
          <span>{moduleInfo?.title || 'Module'}</span>
          <span>/</span>
          <strong>Lessons</strong>
        </nav>

        <div className="edu-header-row">
          <ModulePageHeader
            icon={BookOpen}
            title="Lessons"
            subtitle={`${moduleInfo?.title || 'Module'} — manage lesson metadata (content in next phase)`}
            iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
          />
          <button type="button" className="edu-btn edu-btn--primary" onClick={openCreate}>
            <Plus className="w-4 h-4" />
            Add Lesson
          </button>
        </div>

        <ModuleAlert message={error} />

        <section className="module-card edu-card">
          {lessons.length === 0 ? (
            <div className="edu-empty">
              <BookOpen className="edu-empty__icon" />
              <p>No lessons have been added yet.</p>
              <button type="button" className="edu-btn edu-btn--primary" onClick={openCreate}>
                <Plus className="w-4 h-4" />
                Add Lesson
              </button>
            </div>
          ) : (
            <div className="edu-table-wrap">
              <table className="edu-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Lesson</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th className="edu-table__actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lessons.map((lesson, index) => (
                    <tr key={lesson.id}>
                      <td>{lesson.displayOrder || index + 1}</td>
                      <td className="edu-title-cell">
                        <div>{lesson.title}</div>
                        {lesson.description ? (
                          <span className="edu-module-desc">{lesson.description}</span>
                        ) : null}
                      </td>
                      <td>{lesson.lessonType}</td>
                      <td>
                        <span
                          className={`edu-status ${lesson.isActive ? 'is-active' : 'is-inactive'}`}
                        >
                          {lesson.status}
                        </span>
                      </td>
                      <td>{formatCourseDate(lesson.createdDateTime)}</td>
                      <td className="edu-table__actions">
                        <button
                          type="button"
                          className="edu-icon-btn"
                          title="Move up"
                          disabled={actionBusy || index === 0}
                          onClick={() => moveLesson(lesson, 'up')}
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="edu-icon-btn"
                          title="Move down"
                          disabled={actionBusy || index === lessons.length - 1}
                          onClick={() => moveLesson(lesson, 'down')}
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="edu-icon-btn"
                          title="Edit"
                          onClick={() => openEdit(lesson)}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {lesson.lessonType === 'Quiz' ? (
                          <button
                            type="button"
                            className="edu-icon-btn is-success"
                            title="Manage Quiz"
                            onClick={() =>
                              navigate(
                                `/education/courses/${courseId}/modules/${moduleId}/lessons/${lesson.id}`
                              )
                            }
                          >
                            <FileEdit className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="edu-icon-btn is-success"
                            title="Manage Content"
                            onClick={() =>
                              navigate(
                                `/education/courses/${courseId}/modules/${moduleId}/lessons/${lesson.id}`
                              )
                            }
                          >
                            <FileEdit className="w-4 h-4" />
                          </button>
                        )}
                        {lesson.isActive ? (
                          <button
                            type="button"
                            className="edu-icon-btn"
                            title="Inactivate"
                            onClick={() =>
                              setConfirmAction({ type: 'inactivate', lesson })
                            }
                          >
                            <PowerOff className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="edu-icon-btn is-success"
                            title="Activate"
                            onClick={() =>
                              setConfirmAction({ type: 'activate', lesson })
                            }
                          >
                            <Power className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          className="edu-icon-btn is-danger"
                          title="Delete"
                          onClick={() => setConfirmAction({ type: 'delete', lesson })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {modalOpen ? (
        <div className="edu-modal" role="presentation">
          <button type="button" className="edu-modal__backdrop" onClick={closeModal} aria-label="Close" />
          <div className="edu-modal__panel edu-modal__panel--form" role="dialog" aria-modal="true">
            <div className="edu-modal__head">
              <h2>{editing ? 'Edit Lesson' : 'Add Lesson'}</h2>
              <button type="button" className="edu-icon-btn" onClick={closeModal} disabled={saving}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={saveLesson}>
              <div className="edu-modal__body">
                <div className="edu-field">
                  <label className="edu-label" htmlFor="les-title">
                    Lesson Title *
                  </label>
                  <input
                    id="les-title"
                    className="edu-input"
                    value={form.title}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, title: e.target.value }));
                      if (formError) setFormError('');
                    }}
                    disabled={saving}
                    maxLength={255}
                    required
                    autoFocus
                  />
                </div>
                <div className="edu-field">
                  <label className="edu-label" htmlFor="les-desc">
                    Description
                  </label>
                  <textarea
                    id="les-desc"
                    className="edu-textarea"
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    disabled={saving}
                  />
                </div>
                <div className="edu-field">
                  <label className="edu-label" htmlFor="les-type">
                    Lesson Type *
                  </label>
                  <select
                    id="les-type"
                    className="edu-select"
                    value={form.lessonType}
                    onChange={(e) => setForm((p) => ({ ...p, lessonType: e.target.value }))}
                    disabled={saving || Boolean(editing?.hasContent)}
                    required
                  >
                    {LESSON_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  {editing?.hasContent ? (
                    <span className="edu-muted">
                      Lesson type is locked while content exists. Clear content first to change type.
                    </span>
                  ) : null}
                </div>
                <div className="edu-field">
                  <label className="edu-label" htmlFor="les-order">
                    Display Order
                  </label>
                  <input
                    id="les-order"
                    type="number"
                    min="1"
                    step="1"
                    className="edu-input"
                    value={form.displayOrder}
                    onChange={(e) => setForm((p) => ({ ...p, displayOrder: e.target.value }))}
                    disabled={saving}
                  />
                </div>
                <div className="edu-field">
                  <label className="edu-label" htmlFor="les-status">
                    Status
                  </label>
                  <select
                    id="les-status"
                    className="edu-select"
                    value={form.status}
                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                    disabled={saving}
                  >
                    {COURSE_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                {formError ? <span className="edu-form-error">{formError}</span> : null}
              </div>
              <div className="edu-modal__foot">
                <button type="button" className="edu-btn edu-btn--ghost" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="edu-btn edu-btn--primary" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Update Lesson' : 'Create Lesson'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {confirmAction ? (
        <div className="edu-modal" role="presentation">
          <button
            type="button"
            className="edu-modal__backdrop"
            onClick={() => !actionBusy && setConfirmAction(null)}
            aria-label="Close"
          />
          <div className="edu-modal__panel" role="dialog" aria-modal="true">
            <div className="edu-modal__head">
              <h2>Confirm</h2>
              <button
                type="button"
                className="edu-icon-btn"
                disabled={actionBusy}
                onClick={() => setConfirmAction(null)}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="edu-modal__body">
              <p>
                {confirmCopy} <strong>{confirmAction.lesson?.title}</strong>
              </p>
            </div>
            <div className="edu-modal__foot">
              <button
                type="button"
                className="edu-btn edu-btn--ghost"
                disabled={actionBusy}
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`edu-btn ${
                  confirmAction.type === 'delete' ? 'edu-btn--danger' : 'edu-btn--primary'
                }`}
                disabled={actionBusy}
                onClick={runConfirm}
              >
                {actionBusy ? 'Please wait…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ModuleLessons;
